import { useMemo, useState } from "react";
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  AttachOrganisationsRequestSchema,
  DIRECTORY_PAGE_LIMIT_DEFAULT,
  OrganisationTypeSchema,
} from "@thalamos/haven-common";
import type {
  AttachOrganisationsRequest,
  ConfigurationScopeId,
  ConfigurationScopeSummary,
  Organisation,
  OrganisationId,
  OrganisationScope,
  OrganisationType,
} from "@thalamos/haven-common";

import type { ErrorBody } from "../../api/axios/api.js";
import api, { apiHooks } from "../../api/index.js";
import {
  createRoute,
  ORGANISATION_CONFIGURATION_SCOPE_PARAMETER,
} from "../../routing/createRoute.js";
import { AlsoChanges } from "./AlsoChanges.js";
import { asChains, type ChainEntry } from "./configurationChains.js";
import { ConfirmChangesDialog } from "./ConfirmChangesDialog.js";
import { DirectoryFilter } from "./DirectoryFilter.js";
import { DirectoryTable, type DirectoryColumn } from "./DirectoryTable.js";
import { HolderSettingsPanel } from "./HolderSettingsPanel.js";
import { bothRead, type ReadOutcome } from "./readOutcome.js";
import {
  settingsDifferingBetween,
  type ResolvedSettings,
} from "./settingDifferences.js";
import { UnsavedChangesBar } from "./UnsavedChangesBar.js";
import { useDirectoryQuery } from "./useDirectoryQuery.js";
import {
  organisationEndpoints,
  useHolderConfiguration,
  type HolderEndpoints,
} from "./useHolderConfiguration.js";
import {
  readScopeSettingsFromApi,
  useSettingsOfScopes,
  type ReadScopeSettings,
} from "./useSettingsOfScopes.js";

/** An organisation's configuration changed on the page and not yet saved. */
type UnsavedAttachment = {
  /** The row as it was listed, so it can be shown on any page. */
  organisation: OrganisationScope;
  to: ConfigurationScopeId | null;
};

export type AttachOutcome =
  { kind: "saved" } | { kind: "refused"; message: string | null };

const OwnSettings = ({
  organisationId,
  organisationName,
  ownSettingsEndpoints,
}: {
  organisationId: OrganisationId;
  organisationName: string;
  ownSettingsEndpoints: (organisationId: OrganisationId) => HolderEndpoints;
}) => {
  const { t } = useTranslation();
  // Memoised: useHolderConfiguration reads again whenever its endpoints change.
  const endpoints = useMemo(
    () => ownSettingsEndpoints(organisationId),
    [ownSettingsEndpoints, organisationId],
  );
  const settings = useHolderConfiguration(endpoints);

  return (
    <HolderSettingsPanel
      holderName={organisationName}
      configuration={settings.configuration}
      loading={settings.loading}
      saving={settings.saving}
      error={settings.error}
      appliesTo={t("admin.configuration.settings.unsaved.appliesToOwn", {
        name: organisationName,
      })}
      showChain
      onSave={settings.change}
    />
  );
};

type ConfigurationCellProps = {
  organisation: OrganisationScope;
  configurations: readonly ChainEntry[];
  unsaved: UnsavedAttachment | undefined;
  onChange: (to: ConfigurationScopeId | null) => Promisable<void>;
};

/**
 * Which configuration an organisation is set to.
 *
 * Its own control rather than one more setting, because it changes everything
 * the organisation inherits at once rather than one thing it states.
 */
const ConfigurationCell = ({
  organisation,
  configurations,
  unsaved,
  onChange,
}: ConfigurationCellProps) => {
  const { t } = useTranslation();
  const current =
    unsaved === undefined ? organisation.configurationScopeId : unsaved.to;

  return (
    <Stack spacing={0.5}>
      <Select
        size="small"
        displayEmpty
        value={current ?? ""}
        sx={{ minWidth: 220 }}
        inputProps={{
          "aria-label": t("admin.configuration.organisation.setTo", {
            name: organisation.organisationName,
          }),
        }}
        onChange={(event) => {
          void onChange(
            configurations.find((each) => each.id === event.target.value)?.id ??
              null,
          );
        }}
      >
        <MenuItem value="">
          {t("admin.configuration.organisation.none")}
        </MenuItem>
        {configurations.map((configuration) => (
          <MenuItem
            key={configuration.id}
            value={configuration.id}
            sx={{ pl: 2 + (configuration.level - 1) * 2 }}
          >
            {configuration.name}
          </MenuItem>
        ))}
      </Select>
      {unsaved !== undefined && (
        <Typography
          variant="caption"
          sx={{ color: "warning.dark", fontWeight: 600 }}
        >
          {t("admin.configuration.organisation.unsavedWas", {
            name:
              organisation.configurationScopeName ??
              t("admin.configuration.organisation.none"),
          })}
        </Typography>
      )}
    </Stack>
  );
};

export type OrganisationConfigurationContentProps = {
  rows: readonly OrganisationScope[];
  total: number;
  loading: boolean;
  failed: boolean;
  offset: number;
  onPageChange: (offset: number) => Promisable<void>;
  query: string;
  onQueryChange: (query: string) => Promisable<void>;
  configurationFilter: string | undefined;
  onConfigurationFilterChange: (
    configurationScopeId: string | undefined,
  ) => Promisable<void>;
  typeFilter: OrganisationType | undefined;
  onTypeFilterChange: (type: OrganisationType | undefined) => Promisable<void>;
  /** Keeps the organisations directly a member of this one. */
  memberOfFilter: Organisation | null;
  onMemberOfFilterChange: (
    organisation: Organisation | null,
  ) => Promisable<void>;
  /** What the Member of picker offers, already narrowed to its search. */
  memberOfOptions: readonly Organisation[];
  onMemberOfSearch: (search: string) => Promisable<void>;
  scopes: readonly ConfigurationScopeSummary[];
  onAttachOrganisations: (
    request: AttachOrganisationsRequest,
  ) => Promisable<AttachOutcome>;
  /** How to read and change one organisation's own settings. */
  ownSettingsEndpoints: (organisationId: OrganisationId) => HolderEndpoints;
  /** Reads a configuration's settings, to name what else a move changes. */
  readScopeSettings: ReadScopeSettings;
};

/**
 * The configuration each organisation is set to, with its kind and the
 * organisations it is a member of, so the two hierarchies can be told apart.
 *
 * Changes stay unsaved, across pages and filters, until they are saved together.
 * Settings an organisation states for itself stay reachable from its row: they
 * apply over its configuration, and a value with nowhere to see it would go on
 * applying unexplained.
 */
export const OrganisationConfigurationContent = ({
  rows,
  total,
  loading,
  failed,
  offset,
  onPageChange,
  query,
  onQueryChange,
  configurationFilter,
  onConfigurationFilterChange,
  typeFilter,
  onTypeFilterChange,
  memberOfFilter,
  onMemberOfFilterChange,
  memberOfOptions,
  onMemberOfSearch,
  scopes,
  onAttachOrganisations,
  ownSettingsEndpoints,
  readScopeSettings,
}: OrganisationConfigurationContentProps) => {
  const { t } = useTranslation();
  const [unsaved, setUnsaved] = useState<
    ReadonlyMap<OrganisationId, UnsavedAttachment>
  >(new Map<OrganisationId, UnsavedAttachment>());
  const [unsavedOnly, setUnsavedOnly] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [settingsFor, setSettingsFor] = useState<OrganisationScope | null>(
    null,
  );

  // In chain order, so the picker reads as the chains do.
  const configurations = useMemo(
    () => asChains(scopes).flatMap((chain) => chain.entries),
    [scopes],
  );
  const nameOf = (id: ConfigurationScopeId | null) =>
    configurations.find((each) => each.id === id)?.name ??
    t("admin.configuration.organisation.none");

  const changeAttachment = (
    organisation: OrganisationScope,
    to: ConfigurationScopeId | null,
  ) => {
    setUnsaved((current) => {
      const next = new Map<OrganisationId, UnsavedAttachment>(current);
      const listed =
        current.get(organisation.organisationId)?.organisation ?? organisation;

      if (to === listed.configurationScopeId) {
        next.delete(organisation.organisationId);
      } else {
        next.set(organisation.organisationId, { organisation: listed, to });
      }

      return next;
    });
  };

  const discard = () => {
    setUnsaved(new Map<OrganisationId, UnsavedAttachment>());
    setUnsavedOnly(false);
  };

  const attachments = [...unsaved.values()];
  const shown = unsavedOnly
    ? attachments.map((each) => each.organisation)
    : rows;
  const onThisPage = shown.filter((row) =>
    unsaved.has(row.organisationId),
  ).length;

  // One line per from and to, so the confirmation reads the same however many
  // organisations are moving.
  const moves = [
    ...attachments
      .reduce((groups, each) => {
        const from = each.organisation.configurationScopeId;
        const key = `${from ?? ""}→${each.to ?? ""}`;
        groups.set(key, {
          from,
          to: each.to,
          count: (groups.get(key)?.count ?? 0) + 1,
        });
        return groups;
      }, new Map<string, { from: ConfigurationScopeId | null; to: ConfigurationScopeId | null; count: number }>())
      .values(),
  ];

  // Read only once the confirmation is open: until then nobody asks what else
  // the moves change.
  const settingsOf = useSettingsOfScopes({
    scopeIds: confirming
      ? moves.flatMap((move) =>
          [move.from, move.to].filter((id) => id !== null),
        )
      : [],
    read: readScopeSettings,
  });
  const configurationSettings = (
    id: ConfigurationScopeId | null,
  ): ReadOutcome<ResolvedSettings> =>
    id === null ? { kind: "read", value: null } : settingsOf(id);
  // TODO(organisation settings): a setting an organisation states for itself
  // keeps its value across a move, but comparing the configurations alone still
  // lists it among what the move also changes.
  const otherChanges = (move: (typeof moves)[number]) =>
    bothRead({
      first: configurationSettings(move.from),
      second: configurationSettings(move.to),
      combine: ({ first, second }) =>
        settingsDifferingBetween({
          from: first,
          to: second,
          organisationSettings: null,
        }),
    });
  const changesOfMoves = moves.map((move) => ({
    move,
    changes: otherChanges(move),
  }));

  const save = async () => {
    const request = AttachOrganisationsRequestSchema.safeParse({
      organisations: attachments.map((each) => ({
        organisationId: each.organisation.organisationId,
        configurationScopeId: each.to,
      })),
    });

    // Reachable, by staging more than ORGANISATION_SCOPE_CHANGES_LIMIT
    // organisations across pages, but untested here because driving that in a
    // browser takes minutes. The limit is tested beside its schema instead.
    /* v8 ignore if */
    if (!request.success) {
      setSaveError(
        t("admin.configuration.organisation.saveFailed", {
          reason: t(
            request.error.issues[0]?.message ??
              "admin.configuration.organisation.saveFailedUnknown",
          ),
        }),
      );
      return;
    }

    setSaving(true);
    setSaveError(null);

    const outcome = await onAttachOrganisations(request.data);

    setSaving(false);

    match(outcome)
      .with({ kind: "refused", message: null }, () => {
        setSaveError(t("admin.configuration.organisation.saveFailedUnknown"));
      })
      .with({ kind: "refused" }, ({ message }) => {
        setSaveError(
          t("admin.configuration.organisation.saveFailed", { reason: message }),
        );
      })
      .with({ kind: "saved" }, () => {
        setConfirming(false);
        discard();
      })
      .exhaustive();
  };

  // The total counts every organisation only while nothing narrows the list.
  const unfiltered =
    query.trim().length === 0 &&
    configurationFilter === undefined &&
    typeFilter === undefined &&
    memberOfFilter === null &&
    !unsavedOnly;

  const columns: DirectoryColumn<OrganisationScope>[] = [
    {
      id: "organisation",
      labelKey: "admin.configuration.organisation.name",
      render: (organisation) => (
        <Stack>
          <Link
            component={RouterLink}
            to={createRoute.adminOrganisation(organisation.organisationId)}
          >
            {organisation.organisationName}
          </Link>
          <Typography variant="caption" color="text.secondary">
            {t(`organisation.type.${organisation.organisationType}`)}
          </Typography>
        </Stack>
      ),
    },
    {
      id: "memberOf",
      labelKey: "admin.configuration.organisation.memberOf",
      render: (organisation) =>
        organisation.memberOfNames.length === 0
          ? "—"
          : organisation.memberOfNames.join(", "),
    },
    {
      id: "configuration",
      labelKey: "admin.configuration.organisation.configuration",
      render: (organisation) => (
        <ConfigurationCell
          organisation={
            unsaved.get(organisation.organisationId)?.organisation ??
            organisation
          }
          configurations={configurations}
          unsaved={unsaved.get(organisation.organisationId)}
          onChange={(to) => {
            changeAttachment(organisation, to);
          }}
        />
      ),
    },
    {
      id: "settings",
      labelKey: "admin.configuration.organisation.settings",
      render: (organisation) => (
        <Button
          size="small"
          aria-label={t("admin.configuration.organisation.settingsFor", {
            name: organisation.organisationName,
          })}
          onClick={() => {
            setSettingsFor(organisation);
          }}
        >
          {t("admin.configuration.organisation.settings")}
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="h5" component="h1">
          {t("admin.configuration.organisationsTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {unfiltered && !loading && !failed
            ? t("admin.configuration.organisationsDescriptionCounted", {
                count: total,
              })
            : t("admin.configuration.organisationsDescription")}
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <DirectoryFilter
          value={query}
          label={t("admin.configuration.organisation.filter")}
          onChange={(value) => {
            void onQueryChange(value);
          }}
        />
        <TextField
          select
          size="small"
          label={t("admin.configuration.organisation.configuration")}
          value={configurationFilter ?? ""}
          onChange={(event) => {
            const chosen = event.target.value;
            void onConfigurationFilterChange(
              chosen === "" ? undefined : chosen,
            );
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">
            {t("admin.configuration.organisation.allConfigurations")}
          </MenuItem>
          {configurations.map((configuration) => (
            <MenuItem
              key={configuration.id}
              value={configuration.id}
              sx={{ pl: 2 + (configuration.level - 1) * 2 }}
            >
              {configuration.name}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          options={[...memberOfOptions]}
          value={memberOfFilter}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
          // The server has already narrowed the options to the search.
          filterOptions={(options) => options}
          onInputChange={(_event, value, reason) => {
            // Selecting or leaving the field puts the chosen name in the
            // input, which must not become a search for that name alone.
            if (reason === "input" || reason === "clear") {
              void onMemberOfSearch(value);
            }
          }}
          onChange={(_event, chosen) => {
            void onMemberOfFilterChange(chosen);
          }}
          sx={{ minWidth: 240 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.configuration.organisation.memberOf")}
              placeholder={t("admin.configuration.organisation.memberOfAny")}
              size="small"
            />
          )}
        />
        <TextField
          select
          size="small"
          label={t("admin.configuration.organisation.type")}
          value={typeFilter ?? ""}
          onChange={(event) => {
            const chosen = OrganisationTypeSchema.safeParse(event.target.value);
            void onTypeFilterChange(chosen.success ? chosen.data : undefined);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">
            {t("admin.configuration.organisation.allTypes")}
          </MenuItem>
          {OrganisationTypeSchema.options.map((each) => (
            <MenuItem key={each} value={each}>
              {t(`organisation.type.${each}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            checked={unsavedOnly}
            disabled={unsaved.size === 0}
            onChange={(event) => {
              setUnsavedOnly(event.target.checked);
            }}
          />
        }
        label={t("admin.configuration.organisation.unsavedOnly", {
          count: unsaved.size,
        })}
      />

      <DirectoryTable
        rows={shown}
        total={unsavedOnly ? attachments.length : total}
        loading={!unsavedOnly && loading}
        error={!unsavedOnly && failed}
        columns={columns}
        rowKey={(organisation) => organisation.organisationId}
        limit={
          unsavedOnly
            ? Math.max(attachments.length, 1)
            : DIRECTORY_PAGE_LIMIT_DEFAULT
        }
        offset={unsavedOnly ? 0 : offset}
        onPageChange={(next) => {
          void onPageChange(next);
        }}
        rowSx={(organisation) =>
          unsaved.has(organisation.organisationId)
            ? {
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                boxShadow: (theme) =>
                  `inset 4px 0 0 ${theme.palette.warning.main}`,
              }
            : undefined
        }
      />

      {unsaved.size > 0 && (
        <UnsavedChangesBar
          summary={t("admin.configuration.organisation.unsavedCount", {
            count: unsaved.size,
          })}
          detail={t("admin.configuration.organisation.unsavedWhere", {
            here: onThisPage,
            elsewhere: unsaved.size - onThisPage,
          })}
          discardLabel={t("admin.configuration.organisation.discardAll")}
          disabled={saving}
          onDiscard={discard}
          onSave={() => {
            setSaveError(null);
            setConfirming(true);
          }}
        />
      )}

      <ConfirmChangesDialog
        open={confirming}
        title={t("admin.configuration.organisation.confirmTitle", {
          count: unsaved.size,
        })}
        confirmLabel={t("admin.configuration.organisation.confirm", {
          count: unsaved.size,
        })}
        saving={saving}
        error={saveError}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={save}
        confirmWaits={changesOfMoves.some(
          ({ changes }) => changes.kind === "reading",
        )}
      >
        <Typography variant="body2">
          {t("admin.configuration.organisation.confirmIntro", {
            count: unsaved.size,
          })}
        </Typography>
        <Stack spacing={1}>
          {changesOfMoves.map(({ move, changes }) => (
            <Box
              key={`${move.from ?? ""}→${move.to ?? ""}`}
              sx={{ borderLeft: 3, borderColor: "divider", pl: 1.5 }}
            >
              <Typography variant="body2">
                {t("admin.configuration.organisation.move", {
                  from: nameOf(move.from),
                  to: nameOf(move.to),
                  count: move.count,
                })}
              </Typography>
              <AlsoChanges changes={changes} />
            </Box>
          ))}
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t("admin.configuration.settings.unsaved.noAccessChange")}
        </Typography>
      </ConfirmChangesDialog>

      <Dialog
        open={settingsFor !== null}
        onClose={() => {
          setSettingsFor(null);
        }}
        fullWidth
        maxWidth="lg"
      >
        {settingsFor !== null && (
          <>
            <DialogTitle>
              {t("admin.configuration.organisation.settingsTitle", {
                name: settingsFor.organisationName,
              })}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t("admin.configuration.organisation.settingsHint")}
                </Typography>
                <OwnSettings
                  key={settingsFor.organisationId}
                  organisationId={settingsFor.organisationId}
                  organisationName={settingsFor.organisationName}
                  ownSettingsEndpoints={ownSettingsEndpoints}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => {
                  setSettingsFor(null);
                }}
              >
                {t("admin.configuration.organisation.close")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
};

/**
 * Sends every unsaved change in one request: all are set, or none is. A 4xx
 * resolves (`validateStatus` in `api/axios/api.ts`), so the status is read.
 */
const attachOrganisationsToScopes = async (
  request: AttachOrganisationsRequest,
): Promise<AttachOutcome> => {
  try {
    const response = await api.configuration.attachOrganisations(request);

    if (response.status >= 400) {
      const failure = response.data as ErrorBody | undefined;
      return { kind: "refused", message: failure?.message ?? null };
    }

    return { kind: "saved" };
  } catch {
    return { kind: "refused", message: null };
  }
};

/** The organisation configuration list, loaded and written through the API. */
export const AdminOrganisationConfigurationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { query, input, offset, setQuery, setOffset } = useDirectoryQuery();
  const [type, setType] = useState<OrganisationType>();
  const [memberOf, setMemberOf] = useState<Organisation | null>(null);
  const memberOfQuery = useDirectoryQuery();
  const [{ data: memberOfOptions }] = apiHooks.admin.organisations({
    query: memberOfQuery.query,
    limit: DIRECTORY_PAGE_LIMIT_DEFAULT,
  });

  // Kept in the address, so a link from a configuration's "used by" count
  // opens the list already narrowed to it.
  const configurationScopeId =
    searchParams.get(ORGANISATION_CONFIGURATION_SCOPE_PARAMETER) ?? undefined;

  const [{ data: scopeData }] = apiHooks.configuration.scopes();
  const [{ data, loading, error }, refetch] =
    apiHooks.configuration.organisations({
      query,
      ...(configurationScopeId === undefined ? {} : { configurationScopeId }),
      ...(type === undefined ? {} : { type: [type] }),
      ...(memberOf === null ? {} : { memberOfOrganisationId: memberOf.id }),
      offset,
      limit: DIRECTORY_PAGE_LIMIT_DEFAULT,
    });

  return (
    <OrganisationConfigurationContent
      rows={data?.results ?? []}
      total={data?.total ?? 0}
      loading={loading}
      failed={error !== undefined && error !== null}
      offset={offset}
      onPageChange={setOffset}
      query={input}
      onQueryChange={setQuery}
      configurationFilter={configurationScopeId}
      onConfigurationFilterChange={(chosen) => {
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            if (chosen === undefined) {
              next.delete(ORGANISATION_CONFIGURATION_SCOPE_PARAMETER);
            } else {
              next.set(ORGANISATION_CONFIGURATION_SCOPE_PARAMETER, chosen);
            }
            return next;
          },
          { replace: true },
        );
        setOffset(0);
      }}
      typeFilter={type}
      onTypeFilterChange={(chosen) => {
        setType(chosen);
        setOffset(0);
      }}
      memberOfFilter={memberOf}
      onMemberOfFilterChange={(chosen) => {
        setMemberOf(chosen);
        setOffset(0);
      }}
      memberOfOptions={memberOfOptions?.results ?? []}
      onMemberOfSearch={memberOfQuery.setQuery}
      scopes={scopeData?.scopes ?? []}
      onAttachOrganisations={async (request) => {
        const outcome = await attachOrganisationsToScopes(request);

        await match(outcome)
          // Saved already, so a failed re-read must not turn into a failed
          // save: the listing shows that failure itself.
          .with({ kind: "saved" }, () => refetch().catch(() => undefined))
          .with({ kind: "refused" }, () => undefined)
          .exhaustive();

        return outcome;
      }}
      ownSettingsEndpoints={organisationEndpoints}
      readScopeSettings={readScopeSettingsFromApi}
    />
  );
};
